/**
 * Public, unauthenticated reads against the ATProto network.
 *
 * Everything here runs in the browser, so CORS matters: handle resolution goes
 * through the Bluesky appview (ACAO *), DID documents come from plc.directory,
 * and repo reads go straight to the account's PDS. If a PDS refuses the
 * cross-origin read we surface that instead of silently returning nothing.
 */

const APPVIEW = 'https://public.api.bsky.app';

const PLC_DIRECTORY = 'https://plc.directory';
const UA_TIMEOUT_MS = 20000;

class XrpcError extends Error {
  constructor(message, { status, url } = {}) {
    super(message);
    this.name = 'XrpcError';
    this.status = status;
    this.url = url;
  }
}

async function getJson(url) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), UA_TIMEOUT_MS);
  let res;

  try {
    res = await fetch(url, { signal: controller.signal });
  } catch (err) {
    throw new XrpcError(
      err.name === 'AbortError' ? 'The server took too long to respond.' : 'Network request failed.',
      { url },
    );
  } finally {
    clearTimeout(timer);
  }

  if (!res.ok) {
    let detail = '';

    try {
      const body = await res.json();
      detail = body?.message || body?.error || '';
    } catch {
      /* non-JSON error body */
    }

    throw new XrpcError(detail || `Request failed with status ${res.status}.`, {
      status: res.status,
      url,
    });
  }

  return res.json();
}

export function normaliseAccount(input) {
  const raw = (input || '').trim();

  if (!raw) throw new XrpcError('Enter a handle or DID.');

  if (raw.startsWith('at://')) return raw.slice(5).split('/')[0];

  return raw.replace(/^@/, '').replace(/\/+$/, '');
}

/** Handle or DID to DID. */
export async function resolveDid(input) {
  const account = normaliseAccount(input);

  if (account.startsWith('did:')) return account;

  const data = await getJson(
    `${APPVIEW}/xrpc/com.atproto.identity.resolveHandle?handle=${encodeURIComponent(account)}`,
  );

  return data.did;
}

function didWebUrl(did) {
  const rest = did.slice('did:web:'.length);
  const [host, ...path] = rest.split(':');
  const decodedHost = decodeURIComponent(host);

  if (!path.length) return `https://${decodedHost}/.well-known/did.json`;

  return `https://${decodedHost}/${path.map(decodeURIComponent).join('/')}/did.json`;
}

/** Resolve a DID document for did:plc and did:web. */
export async function getDidDocument(did) {
  if (did.startsWith('did:web:')) return getJson(didWebUrl(did));

  if (did.startsWith('did:plc:')) return getJson(`${PLC_DIRECTORY}/${did}`);
  throw new XrpcError(`Unsupported DID method in ${did}.`);
}

export async function getPdsEndpoint(did) {
  const doc = await getDidDocument(did);

  const service = (doc.service || []).find(
    (s) => s.type === 'AtprotoPersonalDataServer' && s.serviceEndpoint,
  );

  if (!service) throw new XrpcError(`No PDS is listed in the DID document for ${did}.`);

  return service.serviceEndpoint.replace(/\/+$/, '');
}

/** Handle, PDS and collection list for an account. */
export async function describeAccount(input) {
  const did = await resolveDid(input);
  const pds = await getPdsEndpoint(did);
  const repo = await getJson(`${pds}/xrpc/com.atproto.repo.describeRepo?repo=${did}`);

  return {
    did,
    pds,
    handle: repo.handle || did,
    handleIsInvalid: !repo.handle || repo.handle === 'handle.invalid',
    collections: repo.collections || [],
  };
}

export async function listRecords(pds, repo, collection, { limit = 100, cursor } = {}) {
  const params = new URLSearchParams({ repo, collection, limit: String(limit) });

  if (cursor) params.set('cursor', cursor);

  return getJson(`${pds}/xrpc/com.atproto.repo.listRecords?${params}`);
}

/**
 * Page through a collection. `max` bounds the work for accounts with very large
 * collections; the caller is told whether it was truncated.
 */
export async function listRecordsCapped(pds, repo, collection, max = 1000) {
  const records = [];
  let cursor;
  let truncated = false;

  for (;;) {
    const page = await listRecords(pds, repo, collection, { limit: 100, cursor });
    records.push(...(page.records || []));
    cursor = page.cursor;

    if (!cursor || !(page.records || []).length) break;

    if (records.length >= max) {
      truncated = Boolean(cursor);
      break;
    }
  }

  return { records: records.slice(0, max), truncated };
}

/** First record of a collection, or null when the collection is empty. */
export async function firstRecord(pds, repo, collection) {
  const page = await listRecords(pds, repo, collection, { limit: 1 });

  return page.records?.[0] || null;
}

export { XrpcError };
